import { call, put, takeLatest, select } from 'redux-saga/effects';
import type { AccountSummary, Transaction, UserProfile } from '@/types/account.types';
import {
  FETCH_ACCOUNT_SUMMARY_REQUEST,
  FETCH_ACCOUNT_SUMMARY_SUCCESS,
  FETCH_ACCOUNTS_REQUEST,
  FETCH_RECENT_TRANSACTIONS_REQUEST,
  FETCH_USER_PROFILE_REQUEST,
  LOGOUT_REQUEST,
  fetchAccountSummaryFailure,
  fetchAccountSummarySuccess,
  fetchAccountsFailure,
  fetchAccountsSuccess,
  fetchRecentTransactionsFailure,
  fetchRecentTransactionsRequest,
  fetchRecentTransactionsSuccess,
  fetchUserProfileFailure,
  fetchUserProfileSuccess,
  resetAccountState,
} from '@/store/actions/accountActions';
import type { FetchAccountSummaryRequestAction, FetchRecentTransactionsRequestAction } from '@/store/actions/accountActions';
import {
  getAccountSummary,
  getRecentTransactions,
  getUserProfile,
  logout,
} from '@/services/accountApi';
import type { RootState } from '@/store';

function* fetchAccountSummarySaga(action: FetchAccountSummaryRequestAction) {
  try {
    const summary: AccountSummary = yield call(getAccountSummary, action.payload);
    yield put(
      fetchAccountSummarySuccess({
        accounts: summary.accounts,
        selectedAccount: summary.primaryAccount,
      })
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load account summary';
    yield put(fetchAccountSummaryFailure(message));
  }
}

function* fetchAccountsSaga() {
  try {
    const state: RootState = yield select();
    const summary: AccountSummary = yield call(getAccountSummary, state.account.user?.customerId || '');
    yield put(fetchAccountsSuccess(summary.accounts));
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load accounts';
    yield put(fetchAccountsFailure(message));
  }
}

function* fetchRecentTransactionsSaga(action: FetchRecentTransactionsRequestAction) {
  try {
    const transactions: Transaction[] = yield call(getRecentTransactions, action.payload);
    yield put(fetchRecentTransactionsSuccess(transactions));
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load transactions';
    yield put(fetchRecentTransactionsFailure(message));
  }
}

function* fetchAccountSummarySuccessSaga() {
  const state: RootState = yield select();
  const accountNumber = state.account.selectedAccount?.accountNumber;
  if (accountNumber) {
    yield put(fetchRecentTransactionsRequest(accountNumber));
  }
}

function* fetchUserProfileSaga() {
  try {
    const user: UserProfile = yield call(getUserProfile);
    yield put(fetchUserProfileSuccess(user));
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load user profile';
    console.error('[accountSaga] fetchUserProfile failed:', message);
    yield put(fetchUserProfileFailure(message));
  }
}

function* logoutSaga() {
  try {
    yield call(logout);
  } catch (error) {
    console.error('Logout error:', error instanceof Error ? error.message : 'Logout failed');
  } finally {
    yield put(resetAccountState());
  }
}

export default function* watchAccountSaga() {
  yield takeLatest(FETCH_ACCOUNT_SUMMARY_REQUEST, fetchAccountSummarySaga);
  yield takeLatest(FETCH_ACCOUNTS_REQUEST, fetchAccountsSaga);
  yield takeLatest(FETCH_RECENT_TRANSACTIONS_REQUEST, fetchRecentTransactionsSaga);
  yield takeLatest(FETCH_USER_PROFILE_REQUEST, fetchUserProfileSaga);
  yield takeLatest(FETCH_ACCOUNT_SUMMARY_SUCCESS, fetchAccountSummarySuccessSaga);
  yield takeLatest(LOGOUT_REQUEST, logoutSaga);
}
